import {
    collection,
    countUpdates,
    getVariantByParentType,
    parentTypeFor,
    readCount,
    refs,
} from "../_variants/index.js";
import { makeDropdown, next } from "../../../utils";

import {
    revisionQuality
} from "./drawingUtils";

export const addDrawing = ({ form, pageItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = parentTypeFor(variant, "Drawings");
    const drawingRefs = refs(variant, "Drawings");

    form.setValues({ "ATAChapter": pageItem.ATAChapter });
    form.setConfig({
        "title": "Add Drawing",
        "type": "push",
        "location": drawingCollection,
        "fields": [{
            "label": "Parent",
            "type": "value",
            value() {
                return pageItem.Number
            }
        }, {
            "label": "New Drawing Number",
            "type": "value",
            value({ liveInputData, dropdownData }) {
                const selectedATA = dropdownData.ATAChapter.find(ata => {
                    return ata.meta.Number === liveInputData.ATAChapter
                });
                if (!selectedATA) return "";
                const count = readCount(selectedATA.meta, variant, "CountDrawings");
                return `${variant.drawingPrefix}${selectedATA.meta.Number}-${pageItem.AircraftCode || pageItem.Aircraft.split(" -")[0]}-${count}`
            }
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/DrawingTypes"
            }, {
                "label": "ATA Chapter",
                "name": "ATAChapter",
                "type": "dropdown",
                "labelKey": "NameFull",
                "sortKey": "meta.Number",
                "idKey": "Number",
                "dataLocation": "Database/ATAChapters"
            }, {
            "label": "Is Prototype",
            "name": "IsPrototype",
            "type": "dropdown",
            "dropdownData": [{
                "label": "Yes",
                "id": "Yes"
            }, {
                "label": "No",
                "id": "No"
            }]
        }, {
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "col-span-3"
        }],
        async preSubmitHook({ data, dropdownData, liveInputData }) {
            const selectedATA = dropdownData.ATAChapter.find(type => {
                return type?.meta.Number == liveInputData.ATAChapter
            });
            const drawingType = dropdownData.Type.find(ata => {
                return ata?.meta.Name === liveInputData.Type
            });
            const ataCount = readCount(selectedATA.meta, variant, "CountDrawings");

            return {
                "AircraftCode": pageItem.AircraftCode || pageItem.Aircraft.split(" -")[0],
                "ATAChapterID": selectedATA.meta.ID,
                "Aircraft": pageItem.Aircraft || pageItem.AircraftCode,
                "ExpectedTime": drawingType.meta.ExpectedTime,
                "Serial": ataCount,
                "Title": `${drawingType.meta.Name} - ${data.Title}`,
                "Date": {
                    "Created": new Date().toISOString(),
                    "Cancelled": "N/A",
                    "Completed": "N/A"
                },
                "RequiresStressCheck": drawingType.meta.NeedsStress,
                "TypeID": drawingType.meta.ID,
                "Revisions": {
                    "---R1": {
                        "ID": "---R1",
                        "Date": new Date().toISOString(),
                        "Reason": "Drawing Created",
                        "Revision": data.IsPrototype === "No" ? "A" : "P1",
                        "Quality": {
                            "Approver": {
                                "Completed": false,
                                "By": "Not Approved",
                                "Date": "Not Approved"
                            },
                            "Checker": {
                                "Completed": false,
                                "By": "Not Checked",
                                "Date": "Not Checked"
                            },
                            "StressChecker": {
                                "Completed": !drawingType.meta.NeedsStress,
                                "By": drawingType.meta.NeedsStress ? "Not Approved" : "N/A",
                                "Date": drawingType.meta.NeedsStress ? "Not Approved" : "N/A",
                            },
                            "Compiler": {
                                "Completed": false,
                                "By": "Not Completed",
                                "Date": "Not Completed"
                            }
                        }
                    }
                },
                "Revision": data.IsPrototype === "No" ? "A" : "P1",
                "RevisionID": "---R1",
                "Links": {
                    [pageItem.ID]: "---R1"
                },
                "ParentID": pageItem.ID,
                "Parent": pageItem.Number,
                "ParentType": meta.ParentType,
                [meta.ParentType]: {
                    [pageItem.ID]: true
                },
                "Number": `${variant.drawingPrefix}${selectedATA.meta.Number}-${pageItem.AircraftCode || pageItem.Aircraft.split(" -")[0]}-${ataCount}`
            }
        },
        async postSubmitHook({ db, dropdownData, newData, liveInputData, typeRef }) {

            const selectedATA = dropdownData.ATAChapter.find(type => {
                return type?.meta.Number == liveInputData.ATAChapter
            });
            const ataCount = readCount(selectedATA.meta, variant, "CountDrawings");

            await db.set(`Database/${meta.ParentType}/${pageItem.ID}/${drawingCollectionTail}/${typeRef.key}`, true);

            const ataUpdates = countUpdates(
                `Database/ATAChapters/${selectedATA.meta.ID}`,
                variant,
                "CountDrawings",
                ataCount + 1
            );
            await Promise.all(Object.entries(ataUpdates).map(([loc, val]) => db.set(loc, val)));

            await db.utils.updateRefs({ ...newData, ...liveInputData }, drawingRefs, ["Number", "ID", "Date", "Revision", "Parent", "ParentID", "Type", "Title"]);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const editDrawing = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");
    const drawingRefs = refs(variant, "Drawings");

    form.setValues(item);
    form.setConfig({
        "title": "Update Drawing",
        "type": "update",
        "location": `${drawingCollection}/${item.ID}`,
        "fields": [{
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "col-span-2"
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/DrawingTypes"
        }, {
            "label": "Aircraft Code",
            "name": "AircraftCode",
            "sortKey": "meta.Number",
            "type": "dropdown",
            "labelKey": "NameFull",
            "idKey": "Number",
            "dataLocation": "Database/AircraftCodes"
        }],
        preSubmitHook({ data }) {

            if(data.AircraftCode !== item.AircraftCode) {
                // Reconstruct Number preserving any leading drawingPrefix
                // segments (e.g. "M-") — Aircraft Code lives at length-2.
                const split = item.Number.split("-");
                const acIdx = split.length - 2;
                split[acIdx] = data.AircraftCode;
                return {
                    "Number": split.join("-")
                };
            }
        },
        async postSubmitHook({ db, newData }) {
            db.utils.updateRefs(newData, drawingRefs, ["Number", "ID", "Date", "Revision", "Parent", "ParentID", "Type", "Title", "Cancelled", "Completed"]);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const cancelDrawing = ({ form, item, meta, pageItem, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");
    const drawingRefs = refs(variant, "Drawings");

    form.setValues(item);
    form.setConfig({
        "title": "Cancel Drawing",
        "description": `Are you sure you want to cancel drawing ${item.Number}`,
        "type": "update",
        "location": `${drawingCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Cancelled": true,
                "Date" : {
                    "Cancelled": new Date().toISOString()
                }
            }
        },
        postSubmitHook() {
            db.update(`${drawingRefs}/${item.ID}/`, {
                "Cancelled": true
            });
        }

    });
    form.toggle();
};

export const reviseDrawing = ({ form, pageItem, rowItem, meta, store }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    // Calculate the next revision
    let nextRevision = next(rowItem.Revision);

    if (["I", "O", "Q", "S", "X", "Z"].includes(nextRevision)) {
        nextRevision = next(nextRevision);
    };

    let nextRevisionID = `--R${nextRevision}`;

    form.setItem(rowItem);

    form.setConfig({
        "title": "Revise Drawing",
        "description": `Revise Drawing <strong class="text-primary">${rowItem.Number}</strong> from Revision <strong class="text-primary">${rowItem.Revision}</strong> to Revision <strong class="text-primary">${nextRevision}</strong>.<br />Make sure all time has been recorded for this revision.`,
        "location": `${drawingCollection}/${rowItem.ID}`,
        "type": "update",
        "fields": [{
            "label": "Reason",
            "name": `Revisions[${nextRevisionID}][Reason]`,
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/DrawingRevisionTypes",
            "divClass": "cols-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                "Revision": nextRevision,
                "RevisionID": nextRevisionID,
                "Signedoff": false,
                [`Revisions/${nextRevisionID}`]: {
                    "Date": (new Date()).toISOString(),
                    "Revision": nextRevision,
                    "RevisedBy": store.state.user.Basic.name,
                    "ID": nextRevisionID,
                    "Quality": revisionQuality
                },
                "Links": {
                    [pageItem.ID]: nextRevisionID
                }
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const checkDrawing = type => ({ form, item, meta, pageItem, store, db, attachedItems }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setValues(item);
    form.setConfig({
        "title": `${type} Drawing`,
        "description": `<a class="font-bold text-primary cursor-pointer" href="${store.state.handbook}" target="_blank">Open Procedure</a> - Have you read the procedure and does it comply?`,
        "type": "update",
        "location": `${drawingCollection}/${item.ID}`,
        "submitButtonLabel": "I confirm",
        "fields": [{
            "label": "Hours Worked Compiling Drawing",
            "name": "_hoursWorkedCompilingDrawing",
            "type": "number",
            show() {
                return type === "Compiler";
            }
        }, {
            "label": "Job Card",
            "name": "_jobCard",
            "type": "dropdown",
            "dropdownData": makeDropdown(store, attachedItems["JobCards"].items, "Number", "ID"),
            show() {
                return type === "Compiler";
            }
        }],
        preSubmitHook({ data }) {
            return {
                [type]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/By`]: store.getters.userName,
                [`Revisions/${item.RevisionID}/Quality/${type}/Completed`]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/Date`]: (new Date()).toISOString(),
                [`Revisions/${item.RevisionID}/Quality/${type}/HoursWorkedCompilingDrawing`]: data._hoursWorkedCompilingDrawing || 0,
                [`Revisions/${item.RevisionID}/Quality/${type}/JobCardID`]: data._jobCard || null
            }
        },
        async postSubmitHook({ data }) {
            const userID = store.getters.userID;
            const jobCardID = data._jobCard;

            if(!jobCardID) {
                return;
            }
            const jobCard = await db.once(`Database/JobCards/${jobCardID}`);
            console.log(userID, jobCardID, jobCard);
            const usersTimeOnJobCard = jobCard.Time ? parseInt(jobCard.Time[userID] || 0) : 0;
            if (!usersTimeOnJobCard) {
                await db.set(`Database/JobCards/${jobCardID}/Time/${userID}`, data._hoursWorkedCompilingDrawing);
            } else {
                await db.set(`Database/JobCards/${jobCardID}/Time/${userID}`, usersTimeOnJobCard.Time + data._hoursWorkedCompilingDrawing);
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const reviseDrawingToA = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setValues(item);
    form.setConfig({
        "title": "Revise Prototype to Revision A",
        "type": "update",
        "location": `${drawingCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Revision": "A",
                "RevisionID": "---RA",
                "Revisions/---RA": {
                    "Date": new Date().toISOString(),
                    "ID": "---RA",
                    "Quality": revisionQuality,
                    "Reason": "From Prototype to Rev A",
                    "Revision": "A"
                },
                [`Links/${pageItem.ID || item.ParentID}`]: "---RA"
            }
        }
    });
    form.toggle()
};

export const changeLinkedRevision = ({ form, pageItem, rowItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setConfig({
        "title": "Change linked revision",
        "type": "update",
        "location": `${drawingCollection}/${rowItem.ID}/Links/`,
        "fields": [{
            "label": "Revision",
            "name": pageItem.ID,
            "type": "dropdown",
            dropdownData: Object.entries(rowItem.Revisions).map(([key, value]) => ({ id: key, label: value.Revision }))
        }]
    });
    form.getDropdownData();
    form.toggle();
};

export const linkDrawing = ({ form, pageItem, meta, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = parentTypeFor(variant, "Drawings");
    const drawingRefs = refs(variant, "Drawings");

    form.setConfig({
        "title": "Link a Drawing",
        "description": `Link a drawing to ${pageItem.Number}`,
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${drawingCollectionTail}/`,
        "fields": [{
            "label": "Drawing",
            "name": "_doc",
            "type": "dropdown",
            "dataLocation": drawingRefs,
            "labelKey": "Number",
            "idKey": "ID",
            "search": true
        }],
        preSubmitHook({ data }) {
            return {
                "_doc": null,
                [data._doc]: true
            }
        },
        async postSubmitHook({ liveInputData, selectedDropdowns }) {
            const drawing = await db.once(`${drawingCollection}/${liveInputData._doc}`);
            await db.update(`${drawingCollection}/${liveInputData._doc}/`, {
                [meta.ParentType]: {
                    [pageItem.ID]: true
                },
                "Links": {
                    [pageItem.ID]: drawing.RevisionID
                }
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const unlinkDrawing = ({ form, pageItem, rowItem, meta, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = parentTypeFor(variant, "Drawings");

    form.setConfig({
        "title": "Link a Drawing",
        "description": `Unlink a drawing to ${pageItem.Number}`,
        "type": "delete",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${drawingCollectionTail}/${rowItem.ID}`,
        "fields": [],
        postSubmitHook() {
            db.delete(`${drawingCollection}/${rowItem.ID}/${meta.ParentType}/${pageItem.ID}`);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const revisionRollback = ({ form, item, db, meta, pageItem, toast }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    let revisions = Object.values(item.Revisions);

    let previousRevision = revisions[revisions.length - 2];

    if (!previousRevision) {
        toast.warn("No previous revision found");
        return;
    }

    form.setConfig({
        "title": "Rollback Revision",
        "description": `Rollback revision from ${item.Revision} to ${previousRevision.Revision}.`,
        "type": "update",
        "location": `${drawingCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                [`Revisions/${item.RevisionID}`]: null,
                "RevisionID": previousRevision.ID,
                "Revision": previousRevision.Revision
            }
        }
    });
    form.toggle();
};

export const switchParent = async ({ form, item, meta, pageItem, store, attachedItems }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setConfig({
        "title": "Change Parent",
        "type": "update",
        "location": `${drawingCollection}/${item.ID}/`,
        "fields": [{
            "label": "New Parent",
            "type": "dropdown",
            "name": "ParentID",
            "dropdownData": makeDropdown(store, attachedItems[item.ParentType].items, "Number", "ID")
        }],
        preSubmitHook({ selectedDropdowns }) {
            return {
                "Parent": selectedDropdowns.ParentID.label,
                [`Links/${selectedDropdowns.ParentID.id}`]: item.RevisionID
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makePrototype = async ({ form, rowItem, pageItem, meta, store }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setConfig({
        "title": "Make this Drawing a Prototype",
        "type": "update",
        "location": `${drawingCollection}/${rowItem.ID}/`,
        "fields": [],
        preSubmitHook({ data }) {
            return {
                "Revision": "P1",
                "RevisionID": "---P1",
                "Signedoff": false,
                "Revisions": {
                    "---P1": {
                        "Date": (new Date()).toISOString(),
                        "Revision": "P1",
                        "RevisedBy": store.state.user.Basic.name,
                        "Reason": "Set to Prototype",
                        "ID": "---P1",
                        "Quality": revisionQuality
                    }
                },
                "Links": {
                    [pageItem.ID]: "---P1"
                }
            }
        }
    });

    form.toggle();
};

export const toggleStressCheckRequirement = async ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const drawingCollection = collection(variant, "Drawings");

    form.setConfig({
        "title": "Toggle Stress Check Requirement",
        "type": "update",
        "description": `Toggle the stress check requirement to ${!item.RequiresStressCheck ? "Required" : "Not Required"}.`,
        "location": `${drawingCollection}/${item.ID}/`,
        "fields": [],
        preSubmitHook() {
            return {
                "RequiresStressCheck": !item.RequiresStressCheck
            }
        }
    });

    form.toggle();
};
